import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import Navbar from "../components/Navbar";
import { StatusBadge } from "../components/ProductCard";
import { toast } from "sonner";
import {
  Package, Users, ShoppingCart, DollarSign, Plus, Edit2, Trash2, X, Save,
  Shield, AlertTriangle, XCircle
} from "lucide-react";

// Sub-components
const StatsGrid = ({ stats }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
    <StatCard icon={Package} label="Products" value={stats.total_products} />
    <StatCard icon={ShoppingCart} label="Orders" value={stats.total_orders} />
    <StatCard icon={Users} label="Users" value={stats.total_users} />
    <StatCard icon={DollarSign} label="Revenue" value={`$${stats.total_revenue.toFixed(2)}`} />
  </div>
);

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-[#0a0a12] border border-white/5 p-6">
    <div className="flex items-center gap-3 mb-2">
      <Icon className="w-5 h-5 text-[#4F7CFF]" />
      <span className="text-[#A1A1AA] uppercase text-sm tracking-wider">{label}</span>
    </div>
    <span className="text-3xl font-mono text-white">{value}</span>
  </div>
);

const TabButton = ({ active, onClick, icon: Icon, label, testId }) => (
  <button
    onClick={onClick}
    className={`pb-4 px-2 text-sm uppercase tracking-wider flex items-center gap-2 transition-colors ${
      active ? "tab-active" : "tab-inactive"
    }`}
    data-testid={testId}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

const ProductsTable = ({ products, onEdit, onDelete }) => (
  <div className="overflow-x-auto">
    <table className="data-table">
      <thead>
        <tr>
          <th>Product</th>
          <th>Game</th>
          <th>Status</th>
          <th>Prices (D/W/M)</th>
          <th>Active</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {products.map((product) => (
          <tr key={product.id} data-testid={`product-row-${product.id}`}>
            <td className="font-medium text-white">{product.name}</td>
            <td className="text-[#A1A1AA]">{product.game}</td>
            <td><StatusBadge status={product.status} /></td>
            <td className="font-mono text-[#4F7CFF]">
              ${product.price_daily} / ${product.price_weekly} / ${product.price_monthly}
            </td>
            <td>
              <span className={`px-2 py-1 text-xs ${product.is_active ? "bg-[#00FF66]/10 text-[#00FF66]" : "bg-[#FF3B30]/10 text-[#FF3B30]"}`}>
                {product.is_active ? "Yes" : "No"}
              </span>
            </td>
            <td>
              <div className="flex gap-2">
                <button onClick={() => onEdit(product)} className="p-2 text-[#A1A1AA] hover:text-[#4F7CFF] transition-colors" data-testid={`edit-product-${product.id}`}>
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => onDelete(product.id)} className="p-2 text-[#A1A1AA] hover:text-[#FF3B30] transition-colors" data-testid={`delete-product-${product.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const OrdersTable = ({ orders }) => {
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  
  return (
    <div className="overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th>Order ID</th>
            <th>Product</th>
            <th>User</th>
            <th>Duration</th>
            <th>Price</th>
            <th>Status</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.id} data-testid={`order-row-${order.id}`}>
              <td className="font-mono text-[#A1A1AA] text-sm">{order.id.slice(0, 8)}...</td>
              <td className="font-medium text-white">{order.product_name}</td>
              <td className="text-[#A1A1AA]">{order.user_id.slice(0, 8)}...</td>
              <td className="uppercase text-[#A1A1AA]">{order.duration}</td>
              <td className="font-mono text-[#4F7CFF]">${order.price}</td>
              <td>
                <span className={`px-2 py-1 text-xs uppercase ${order.status === "completed" ? "bg-[#00FF66]/10 text-[#00FF66]" : "bg-[#F5A623]/10 text-[#F5A623]"}`}>
                  {order.status}
                </span>
              </td>
              <td className="text-[#A1A1AA]">{formatDate(order.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const UsersTable = ({ users, onToggleAdmin }) => {
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  
  return (
    <div className="overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Email</th>
            <th>Role</th>
            <th>Joined</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.id}`}>
              <td className="font-medium text-white">{u.username}</td>
              <td className="text-[#A1A1AA]">{u.email}</td>
              <td>
                <span className={`px-2 py-1 text-xs uppercase ${u.is_admin ? "bg-[#4F7CFF]/10 text-[#4F7CFF]" : "bg-white/5 text-[#A1A1AA]"}`}>
                  {u.is_admin ? "Admin" : "User"}
                </span>
              </td>
              <td className="text-[#A1A1AA]">{formatDate(u.created_at)}</td>
              <td>
                <button onClick={() => onToggleAdmin(u.id)} className="btn-secondary px-3 py-1 text-xs" data-testid={`toggle-admin-${u.id}`}>
                  {u.is_admin ? "Remove Admin" : "Make Admin"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ProductModal = ({ product, onClose, onSave, token }) => {
  const [formData, setFormData] = useState({
    name: product?.name || "",
    game: product?.game || "",
    description: product?.description || "",
    features: product?.features?.join(", ") || "",
    image_url: product?.image_url || "",
    status: product?.status || "undetected",
    price_daily: product?.price_daily || 4.99,
    price_weekly: product?.price_weekly || 14.99,
    price_monthly: product?.price_monthly || 29.99,
    is_active: product?.is_active ?? true
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      ...formData,
      features: formData.features.split(",").map(f => f.trim()).filter(f => f),
      price_daily: parseFloat(formData.price_daily),
      price_weekly: parseFloat(formData.price_weekly),
      price_monthly: parseFloat(formData.price_monthly)
    };

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = product
        ? await axios.put(`${API}/admin/products/${product.id}`, payload, { headers })
        : await axios.post(`${API}/admin/products`, payload, { headers });
      
      onSave(response.data);
      toast.success(product ? "Product updated" : "Product created");
    } catch {
      toast.error("Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const statusOptions = [
    { value: "undetected", label: "Undetected", icon: Shield, color: "#00FF66" },
    { value: "updating", label: "Updating", icon: AlertTriangle, color: "#F5A623" },
    { value: "detected", label: "Detected", icon: XCircle, color: "#FF3B30" }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="product-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0a0a12] border border-white/5 p-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#A1A1AA] hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>

        <h2 className="font-heading text-2xl text-white mb-6">{product ? "Edit Product" : "Add Product"}</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2 uppercase tracking-wider">Name</label>
              <input type="text" value={formData.name} onChange={(e) => updateField("name", e.target.value)} required data-testid="product-name-input" />
            </div>
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2 uppercase tracking-wider">Game</label>
              <input type="text" value={formData.game} onChange={(e) => updateField("game", e.target.value)} required data-testid="product-game-input" />
            </div>
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-2 uppercase tracking-wider">Description</label>
            <textarea value={formData.description} onChange={(e) => updateField("description", e.target.value)} rows={3} required data-testid="product-description-input" />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-2 uppercase tracking-wider">Features (comma-separated)</label>
            <input type="text" value={formData.features} onChange={(e) => updateField("features", e.target.value)} placeholder="Aimbot, ESP, No Recoil" data-testid="product-features-input" />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-2 uppercase tracking-wider">Image URL</label>
            <input type="url" value={formData.image_url} onChange={(e) => updateField("image_url", e.target.value)} placeholder="https://..." data-testid="product-image-input" />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-2 uppercase tracking-wider">Status</label>
            <div className="flex gap-3">
              {statusOptions.map(({ value, label, icon: Icon, color }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => updateField("status", value)}
                  className={`flex-1 flex items-center justify-center gap-2 p-3 border transition-colors ${
                    formData.status === value
                      ? `border-[${color}] bg-[${color}]/10 text-[${color}]`
                      : "border-white/10 text-[#A1A1AA] hover:border-white/30"
                  }`}
                  style={formData.status === value ? { borderColor: color, backgroundColor: `${color}15`, color } : {}}
                  data-testid={`status-${value}`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2 uppercase tracking-wider">Daily Price</label>
              <input type="number" step="0.01" min="0" value={formData.price_daily} onChange={(e) => updateField("price_daily", e.target.value)} required data-testid="price-daily-input" />
            </div>
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2 uppercase tracking-wider">Weekly Price</label>
              <input type="number" step="0.01" min="0" value={formData.price_weekly} onChange={(e) => updateField("price_weekly", e.target.value)} required data-testid="price-weekly-input" />
            </div>
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2 uppercase tracking-wider">Monthly Price</label>
              <input type="number" step="0.01" min="0" value={formData.price_monthly} onChange={(e) => updateField("price_monthly", e.target.value)} required data-testid="price-monthly-input" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => updateField("is_active", !formData.is_active)}
              className={`w-12 h-6 rounded-full transition-colors relative ${formData.is_active ? "bg-[#4F7CFF]" : "bg-white/10"}`}
              data-testid="product-active-toggle"
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.is_active ? "left-7" : "left-1"}`} />
            </button>
            <span className="text-[#A1A1AA]">Product Active</span>
          </div>

          <div className="flex gap-4 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="save-product-btn">
              {saving ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</> : <><Save className="w-4 h-4" />Save Product</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("products");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const headers = useMemo(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, productsRes, ordersRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/products`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/admin/users`, { headers })
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setUsers(usersRes.data);
    } catch {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, { headers });
      setProducts(prev => prev.filter(p => p.id !== productId));
      toast.success("Product deleted");
    } catch {
      toast.error("Failed to delete product");
    }
  };

  const handleToggleAdmin = async (userId) => {
    try {
      const response = await axios.put(`${API}/admin/users/${userId}/toggle-admin`, {}, { headers });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_admin: response.data.is_admin } : u));
      toast.success("User updated");
    } catch {
      toast.error("Failed to update user");
    }
  };

  const handleSaveProduct = (savedProduct) => {
    if (editingProduct) {
      setProducts(prev => prev.map(p => p.id === savedProduct.id ? savedProduct : p));
    } else {
      setProducts(prev => [...prev, savedProduct]);
    }
    setShowProductModal(false);
    setEditingProduct(null);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setShowProductModal(true);
  };

  const openAddModal = () => {
    setEditingProduct(null);
    setShowProductModal(true);
  };

  return (
    <div className="min-h-screen bg-[#050508]" data-testid="admin-page">
      <Navbar />

      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h1 className="font-heading text-3xl sm:text-4xl text-white mb-2">Admin Dashboard</h1>
            <p className="text-[#A1A1AA]">Manage products, orders, and users</p>
          </div>

          {stats && <StatsGrid stats={stats} />}

          <div className="flex gap-4 border-b border-white/10 mb-6">
            <TabButton active={activeTab === "products"} onClick={() => setActiveTab("products")} icon={Package} label="Products" testId="admin-tab-products" />
            <TabButton active={activeTab === "orders"} onClick={() => setActiveTab("orders")} icon={ShoppingCart} label="Orders" testId="admin-tab-orders" />
            <TabButton active={activeTab === "users"} onClick={() => setActiveTab("users")} icon={Users} label="Users" testId="admin-tab-users" />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#4F7CFF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {activeTab === "products" && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="font-heading text-xl text-white">Products ({products.length})</h2>
                    <button onClick={openAddModal} className="btn-primary flex items-center gap-2 text-sm" data-testid="add-product-btn">
                      <Plus className="w-4 h-4" />Add Product
                    </button>
                  </div>
                  <ProductsTable products={products} onEdit={openEditModal} onDelete={handleDeleteProduct} />
                </div>
              )}

              {activeTab === "orders" && (
                <div>
                  <h2 className="font-heading text-xl text-white mb-6">Orders ({orders.length})</h2>
                  <OrdersTable orders={orders} />
                </div>
              )}

              {activeTab === "users" && (
                <div>
                  <h2 className="font-heading text-xl text-white mb-6">Users ({users.length})</h2>
                  <UsersTable users={users} onToggleAdmin={handleToggleAdmin} />
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {showProductModal && (
        <ProductModal
          product={editingProduct}
          onClose={() => { setShowProductModal(false); setEditingProduct(null); }}
          onSave={handleSaveProduct}
          token={token}
        />
      )}
    </div>
  );
};

export default AdminPage;
